import { useEffect, useState } from "react";
import { Wallet2, Loader2, RefreshCw, Percent, Save } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatRupiah } from "@/components/property/roomMeta";
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES } from "@/components/booking/bookingMeta";

export default function AdminCommissionsPage() {
  const [config, setConfig] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [rateInput, setRateInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const loadReport = () => {
    setLoading(true); setError(false);
    const params = {};
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    Promise.all([
      apiClient.get("/admin/commission-config"),
      apiClient.get("/admin/commissions/report", { params }),
    ])
      .then(([c, r]) => { setConfig(c.data); setReport(r.data); if (rateInput === "") setRateInput(String(c.data.commission_rate)); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadReport(); /* eslint-disable-next-line */ }, []);

  const saveRate = async () => {
    const rate = Number(rateInput);
    if (isNaN(rate) || rate < 0 || rate > 100) { toast.error("Rate harus 0–100."); return; }
    setSaving(true);
    try {
      await apiClient.post("/admin/commission-config", { commission_rate: rate, status: "active" });
      toast.success(`Rate komisi diperbarui ke ${rate}%. Transaksi lama tidak berubah.`);
      loadReport();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const t = report?.totals;

  return (
    <AdminLayout testid="admin-commissions-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Komisi Platform</h1>
          <p className="mt-2 text-sm text-muted-foreground">Konfigurasi rate & laporan komisi seluruh platform. Komisi hanya dari room payment yang berhasil; deposit tidak dikenakan.</p>
        </div>
        <button onClick={loadReport} data-testid="admin-commissions-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
          <RefreshCw size={14} /> Muat Ulang
        </button>
      </div>

      {/* Rate config */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-5" data-testid="admin-commission-config">
        <div className="flex items-center gap-2 text-sm font-semibold"><Percent size={16} className="text-primary" /> Rate Komisi Aktif</div>
        <div className="mt-3 flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs font-medium mb-1 text-muted-foreground">Rate (%)</label>
            <input type="number" min={0} max={100} step="0.5" value={rateInput} onChange={(e) => setRateInput(e.target.value)}
              data-testid="admin-commission-rate-input"
              className="w-32 rounded-xl border border-border bg-background px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
          </div>
          <button onClick={saveRate} disabled={saving} data-testid="admin-commission-save"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Simpan Rate
          </button>
          {config && <span className="text-xs text-muted-foreground">Berlaku sejak {config.effective_date} · perubahan tidak mengubah transaksi lama.</span>}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-4 flex items-end gap-3 flex-wrap">
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">Dari Tanggal</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="admin-commission-date-from"
            className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1 text-muted-foreground">Sampai Tanggal</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="admin-commission-date-to"
            className="rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary/30" />
        </div>
        <button onClick={loadReport} data-testid="admin-commission-apply-filter"
          className="px-4 py-2 rounded-full text-sm font-semibold border border-border hover:bg-secondary/60 transition-colors">Terapkan</button>
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="admin-commissions-error">
          <p className="text-sm text-destructive">Gagal memuat laporan komisi.</p>
          <button onClick={loadReport} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-commissions-summary">
            <Card label="Total Booking" value={String(t.booking_count)} />
            <Card label="Gross Room" value={formatRupiah(t.gross_room)} />
            <Card label="Total Diskon" value={`-${formatRupiah(t.discount)}`} tone="emerald" />
            <Card label="Net Room" value={formatRupiah(t.net_room)} />
            <Card label="Room Dibayar" value={formatRupiah(t.room_paid)} />
            <Card label="Komisi Platform" value={formatRupiah(t.platform_commission)} tone="primary" highlight />
            <Card label="Owner Room Payable" value={formatRupiah(t.owner_room_payable)} />
            <Card label="Deposit Dibayar" value={formatRupiah(t.deposit_paid)} />
          </div>

          <div className="mt-8 grid lg:grid-cols-2 gap-6">
            <GroupTable title="Komisi per Owner" rows={report.by_owner} testid="admin-commissions-by-owner" />
            <GroupTable title="Komisi per Property" rows={report.by_property} testid="admin-commissions-by-property" />
          </div>

          <h2 className="mt-8 font-heading text-lg font-semibold">Komisi per Booking</h2>
          {t.booking_count === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center" data-testid="admin-commissions-empty">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary mx-auto mb-3"><Wallet2 size={22} /></span>
              <p className="text-sm text-muted-foreground">Belum ada komisi tercatat pada rentang ini.</p>
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-2xl border border-border" data-testid="admin-commissions-table">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-3 py-3 font-semibold">Booking</th>
                    <th className="px-3 py-3 font-semibold">Owner</th>
                    <th className="px-3 py-3 font-semibold text-right">Net Room</th>
                    <th className="px-3 py-3 font-semibold text-right">Room Dibayar</th>
                    <th className="px-3 py-3 font-semibold text-right">Komisi</th>
                    <th className="px-3 py-3 font-semibold text-right">Owner Room</th>
                  </tr>
                </thead>
                <tbody>
                  {report.bookings.map((b, i) => (
                    <tr key={b.booking_id} data-testid={`admin-commission-row-${i}`} className="border-t border-border">
                      <td className="px-3 py-3">
                        <div className="font-medium">{b.property_name || "Property"}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-xs text-muted-foreground">{b.booking_id.slice(0, 8)}</span>
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${BOOKING_STATUS_STYLES[b.booking_status] || ""}`}>{BOOKING_STATUS_LABELS[b.booking_status] || b.booking_status}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3">{b.owner_name || "-"}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.net_room)}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.room_paid)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-primary">{formatRupiah(b.platform_commission)}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.owner_room_payable)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
}

function Card({ label, value, tone, highlight }) {
  const toneCls = tone === "primary" ? "text-primary" : tone === "emerald" ? "text-emerald-600" : "text-foreground";
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-lg font-bold ${toneCls}`}>{value}</p>
    </div>
  );
}

function GroupTable({ title, rows, testid }) {
  return (
    <div>
      <h3 className="font-heading text-base font-semibold mb-2">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <p className="text-sm text-muted-foreground">Belum ada data.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border" data-testid={testid}>
          <table className="w-full text-sm">
            <thead className="bg-secondary/50 text-muted-foreground">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-semibold">Nama</th>
                <th className="px-3 py-2.5 font-semibold text-right">Room Dibayar</th>
                <th className="px-3 py-2.5 font-semibold text-right">Komisi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t border-border">
                  <td className="px-3 py-2.5">{r.label || r.key.slice(0, 8)}</td>
                  <td className="px-3 py-2.5 text-right">{formatRupiah(r.room_paid)}</td>
                  <td className="px-3 py-2.5 text-right font-semibold text-primary">{formatRupiah(r.platform_commission)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
